import {
  AnchorHTMLAttributes,
  FC,
  memo,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AiActivityCard } from '../AiActivityCard';
import { ActivityData, parseActivities } from '../parseActivities';
import { useInternalLinkHandler } from '../useInternalLinkHandler';
import { Markdown, TaskList } from './styles';

const TYPE_INTERVAL_MS = 15;
/** Respostas longas aparecem em no máximo ~300 passos. */
const MAX_TYPE_STEPS = 300;

type Block = { type: 'text'; text: string } | { type: 'tasks'; tasks: ActivityData[] };

const toBlocks = (content: string): Block[] =>
  parseActivities(content).reduce<Block[]>((blocks, segment) => {
    const last = blocks[blocks.length - 1];
    if (segment.type === 'activity' && segment.activity) {
      if (last?.type === 'tasks') {
        last.tasks.push(segment.activity);
      } else {
        blocks.push({ type: 'tasks', tasks: [segment.activity] });
      }
    } else if (segment.text?.trim()) {
      blocks.push({ type: 'text', text: segment.text });
    }
    return blocks;
  }, []);

/** Revela o texto aos poucos quando a resposta chega inteira (thread nova, sem streaming). */
const useTypewriter = (content: string, animate: boolean) => {
  const [length, setLength] = useState(animate ? 0 : content.length);
  const animatedRef = useRef(animate);

  useEffect(() => {
    if (!animatedRef.current) {
      setLength(content.length);
      return;
    }

    if (length >= content.length) {
      animatedRef.current = false;
      return;
    }

    const timeout = setTimeout(
      () =>
        setLength(
          (current) => current + Math.max(2, Math.ceil(content.length / MAX_TYPE_STEPS))
        ),
      TYPE_INTERVAL_MS
    );
    return () => clearTimeout(timeout);
  }, [content, length]);

  return content.slice(0, length);
};

export interface AnswerContentProps {
  content: string;
  animate?: boolean;
}

export const AnswerContent: FC<AnswerContentProps> = memo(
  ({ content, animate = false }) => {
    const handleLinkClick = useInternalLinkHandler();
    const displayed = useTypewriter(content, animate);

    const components = {
      a: ({
        children,
        ...props
      }: AnchorHTMLAttributes<HTMLAnchorElement> & { children?: ReactNode }) => (
        <a {...props} onClick={handleLinkClick}>
          {children}
        </a>
      )
    };

    return (
      <Markdown>
        {toBlocks(displayed).map((block, index) =>
          block.type === 'tasks' ? (
            <TaskList key={index}>
              {block.tasks.map((task, taskIndex) => (
                <AiActivityCard key={task.number ?? taskIndex} {...task} />
              ))}
            </TaskList>
          ) : (
            <ReactMarkdown
              key={index}
              remarkPlugins={[remarkGfm]}
              components={components}
            >
              {block.text}
            </ReactMarkdown>
          )
        )}
      </Markdown>
    );
  }
);

AnswerContent.displayName = 'AnswerContent';
